<template>
  <Teleport to="body">
    <div class="spotlight-tour">
      <!-- Full-viewport dim layer. When a target was found, a "cutout" div
           (positioned exactly over the target's rect) uses the classic
           box-shadow spotlight trick — its own giant shadow IS the dim
           layer, so only ONE element is needed for both the dim + cutout. -->
      <div
        v-if="targetRect"
        class="spotlight-cutout"
        :style="cutoutStyle"
      ></div>
      <div v-else class="spotlight-dim"></div>

      <div
        ref="cardEl"
        class="spotlight-card"
        :class="{ 'spotlight-card--with-image': !!currentStep.image }"
        :style="cardStyle"
      >
        <img v-if="currentStep.image" :src="currentStep.image" class="spotlight-card__image" alt="" />
        <h3 class="spotlight-card__title">{{ currentStep.title }}</h3>
        <p class="spotlight-card__description">{{ effectiveDescription }}</p>

        <!-- Before/after mini-preview — e.g. "Free planning" -> "Locked",
             styled to mirror the real guided-toolbar badges so it reads as
             an authentic example, not a generic illustration. -->
        <div v-if="currentStep.example" class="spotlight-card__example">
          <div class="spotlight-card__example-chip">
            <span
              class="spotlight-card__example-badge"
              :class="`spotlight-card__example-badge--${currentStep.example.before.variant || 'muted'}`"
            >
              <span class="material-icons" aria-hidden="true">{{ currentStep.example.before.icon }}</span>
            </span>
            <span class="spotlight-card__example-label">{{ currentStep.example.before.label }}</span>
          </div>
          <span class="material-icons spotlight-card__example-arrow" aria-hidden="true">arrow_forward</span>
          <div class="spotlight-card__example-chip">
            <span
              class="spotlight-card__example-badge"
              :class="`spotlight-card__example-badge--${currentStep.example.after.variant || 'muted'}`"
            >
              <span class="material-icons" aria-hidden="true">{{ currentStep.example.after.icon }}</span>
            </span>
            <span class="spotlight-card__example-label">{{ currentStep.example.after.label }}</span>
          </div>
        </div>

        <div class="spotlight-card__footer">
          <div class="spotlight-card__dots">
            <span
              v-for="(_, i) in steps"
              :key="i"
              class="spotlight-card__dot"
              :class="{ 'spotlight-card__dot--active': i === currentIndex }"
            ></span>
          </div>
          <div class="spotlight-card__actions">
            <button
              v-if="currentIndex > 0"
              type="button"
              class="spotlight-card__btn spotlight-card__btn--ghost"
              @click="back"
            >Back</button>
            <button type="button" class="spotlight-card__btn spotlight-card__btn--skip" @click="skip">
              Skip tour
            </button>
            <button type="button" class="spotlight-card__btn spotlight-card__btn--primary" @click="next">
              {{ isLastStep ? 'Done' : 'Next' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';

export interface SpotlightExampleState {
  /** Material icon name. */
  icon: string;
  label: string;
  /** 'muted' (grey badge) or 'active' (brand-yellow badge) — mirrors the
   *  real guided-toolbar unlocked/locked badge colors. Defaults to 'muted'. */
  variant?: 'muted' | 'active';
}

export interface SpotlightStep {
  /** CSS selector for the element to highlight. Omit for a centered, non-anchored step. */
  selector?: string;
  title: string;
  description: string;
  /** Shown above the title (e.g. an intro gif). */
  image?: string;
  /** Shown instead of `description` when `selector` doesn't resolve to a live element. */
  fallbackNote?: string;
  /** A small "before -> after" state preview shown under the description. */
  example?: { before: SpotlightExampleState; after: SpotlightExampleState };
}

const props = defineProps<{ steps: SpotlightStep[] }>();
const emit = defineEmits<{ done: [] }>();

const currentIndex = ref(0);
const targetRect = ref<DOMRect | null>(null);
const cardEl = ref<HTMLElement | null>(null);
let targetEl: HTMLElement | null = null;
let resolveToken = 0;

const currentStep = computed(() => props.steps[currentIndex.value]);
const isLastStep = computed(() => currentIndex.value === props.steps.length - 1);
const effectiveDescription = computed(() => {
  const step = currentStep.value;
  return (!targetRect.value && step.fallbackNote) ? step.fallbackNote : step.description;
});

const SPOTLIGHT_PADDING = 8;
const CARD_GAP = 16;
const CARD_ESTIMATED_WIDTH = 360;
const CARD_ESTIMATED_HEIGHT = 240;
const VIEWPORT_MARGIN = 12;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

const cutoutStyle = computed(() => {
  const rect = targetRect.value;
  if (!rect) return {};
  return {
    top: `${rect.top - SPOTLIGHT_PADDING}px`,
    left: `${rect.left - SPOTLIGHT_PADDING}px`,
    width: `${rect.width + SPOTLIGHT_PADDING * 2}px`,
    height: `${rect.height + SPOTLIGHT_PADDING * 2}px`,
  };
});

const cardStyle = computed(() => {
  const rect = targetRect.value;
  if (!rect) {
    // No live target this step — center the card in the viewport.
    return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const left = clamp(
    rect.left + rect.width / 2 - CARD_ESTIMATED_WIDTH / 2,
    VIEWPORT_MARGIN,
    Math.max(VIEWPORT_MARGIN, viewportWidth - CARD_ESTIMATED_WIDTH - VIEWPORT_MARGIN)
  );

  const spaceBelow = viewportHeight - rect.bottom;
  const placeBelow = spaceBelow >= CARD_ESTIMATED_HEIGHT + CARD_GAP || spaceBelow >= rect.top;

  if (placeBelow) {
    return { left: `${left}px`, top: `${rect.bottom + SPOTLIGHT_PADDING + CARD_GAP}px` };
  }
  return { left: `${left}px`, bottom: `${viewportHeight - rect.top + SPOTLIGHT_PADDING + CARD_GAP}px` };
});

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function resolveStep() {
  const token = ++resolveToken;
  const step = currentStep.value;
  targetEl = null;

  if (!step.selector) {
    targetRect.value = null;
    return;
  }

  for (let attempt = 0; attempt < 5; attempt++) {
    if (token !== resolveToken) return; // a later step started resolving — abandon this one
    await nextTick();
    const el = document.querySelector<HTMLElement>(step.selector);
    if (el) {
      targetEl = el;
      targetRect.value = el.getBoundingClientRect();
      return;
    }
    await delay(150);
  }

  if (token !== resolveToken) return;
  targetRect.value = null;
}

function remeasure() {
  if (targetEl) {
    targetRect.value = targetEl.getBoundingClientRect();
  }
}

function next() {
  if (isLastStep.value) {
    finish();
    return;
  }
  currentIndex.value += 1;
  resolveStep();
}

function back() {
  if (currentIndex.value === 0) return;
  currentIndex.value -= 1;
  resolveStep();
}

function skip() {
  finish();
}

function finish() {
  emit('done');
}

onMounted(() => {
  resolveStep();
  window.addEventListener('resize', remeasure);
  window.addEventListener('scroll', remeasure, true);
});

onBeforeUnmount(() => {
  resolveToken += 1; // abandon any in-flight resolveStep retry loop
  window.removeEventListener('resize', remeasure);
  window.removeEventListener('scroll', remeasure, true);
});
</script>

<style scoped>
.spotlight-tour {
  position: fixed;
  inset: 0;
  z-index: 2000;
}

.spotlight-dim {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
}

.spotlight-cutout {
  position: fixed;
  border-radius: 12px;
  border: 2px solid #ffd600;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.75);
  pointer-events: none;
  transition: top 0.25s ease, left 0.25s ease, width 0.25s ease, height 0.25s ease;
  animation: spotlight-pulse 2s ease-in-out infinite;
}

@keyframes spotlight-pulse {
  0%, 100% { outline: 0 solid rgba(255, 214, 0, 0.45); }
  50% { outline: 8px solid rgba(255, 214, 0, 0); }
}

.spotlight-card {
  position: fixed;
  width: 360px;
  max-width: calc(100vw - 24px);
  background-color: #1b1b1d;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
  transition: top 0.25s ease, left 0.25s ease, bottom 0.25s ease, width 0.25s ease;
}

/* The intro step's gif needs real room to be legible — noticeably wider
   than the plain text/highlight steps. */
.spotlight-card--with-image {
  width: 560px;
}

.spotlight-card__image {
  width: 100%;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  display: block;
}

.spotlight-card__title {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0 0 0.4rem;
}

.spotlight-card__description {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.75);
  margin: 0 0 1rem;
  line-height: 1.4;
}

.spotlight-card__example {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.75rem 0.5rem;
  margin: 0 0 1rem;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.spotlight-card__example-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
}

.spotlight-card__example-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
}

/* Matches PlanTrailRoutes.vue's guided-toolbar__icon-badge colors exactly,
   so this reads as the real UI rather than a generic illustration. */
.spotlight-card__example-badge--muted {
  background: rgba(255, 255, 255, 0.08);
  color: #a1a4ad;
}

.spotlight-card__example-badge--active {
  background: rgba(253, 223, 0, 0.14);
  color: #fddf00;
}

.spotlight-card__example-badge .material-icons {
  font-size: 16px;
}

.spotlight-card__example-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
}

.spotlight-card__example-arrow {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.35);
}

.spotlight-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.spotlight-card__dots {
  display: flex;
  gap: 6px;
}

.spotlight-card__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  transition: background 0.2s ease, transform 0.2s ease;
}

.spotlight-card__dot--active {
  background: #ffd600;
  transform: scale(1.3);
}

.spotlight-card__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.spotlight-card__btn {
  border-radius: 8px;
  padding: 0.4rem 0.85rem;
  font-size: 0.85rem;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.spotlight-card__btn--ghost {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.spotlight-card__btn--ghost:hover {
  background: rgba(255, 255, 255, 0.1);
}

.spotlight-card__btn--skip {
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
}

.spotlight-card__btn--skip:hover {
  color: rgba(255, 255, 255, 0.9);
}

.spotlight-card__btn--primary {
  background-color: #ffd600;
  border-color: #ffd600;
  color: #000;
}

.spotlight-card__btn--primary:hover {
  background-color: #ffca00;
  border-color: #ffca00;
}
</style>
